import { useSelector } from 'react-redux';
import { useShallowSelector } from '../app/hooks';
import { RootState } from '../app/store';
import { OrderStatus } from '../reducers/orderSlice.constants';
import { ITransactions } from '../reducers/orderSlice.props';
import { getCodeNameMapping } from '../selectors/menu';
import { CartItem, CartModifierGroup } from './cart';
import { OH } from './constants';
import { ModSymbolCodeNameMappingType, READ_REP_MAP } from './mappings';
import { sortChildrenModGroup } from './menu';
import { printCurrency } from './money';
import { BRANDS_REQUIRE_LONG_MESSAGE_WITH_TOTAL } from '../utils/constants';
import { TOTAL_PLACEHOLDER } from '../constants';

const SIZE_MODIFIERS = [
  'Small',
  'Medium',
  'Large',
  'Regular',
  'sml',
  'med',
  'lge',
];

const DEFAULT_TOTAL_COMPLETE_MESSAGE =
  'Please go ahead and pull up to the window for your total.';

export function getMessageWithTotal({
  message,
  ITEM_BY_ITEM,
  currentTransactionId,
  transactions,
  completeClickCount,
  total,
  allCouponApplied,
  title,
  restaurantCode,
  alreadySpokenMessage,
}: {
  message: string;
  ITEM_BY_ITEM: boolean;
  currentTransactionId: string | null;
  transactions: ITransactions;
  completeClickCount: {
    [transaction_id: string]: number;
  };
  total: number | null;
  allCouponApplied: boolean;
  title?: string;
  restaurantCode: string;
  // HITL-1570: when the donation upsell already spoke the check total, the
  // total-less `already_spoken_total_readback` prompt to use instead of
  // substituting the total. It only replaces the readback where the total
  // would actually have been spoken — every guard below (item-by-item off,
  // transaction not completed, coupons pending) still falls back to the
  // default pull-forward message exactly as before.
  alreadySpokenMessage?: string;
}) {
  if (
    !ITEM_BY_ITEM ||
    (currentTransactionId &&
      transactions[currentTransactionId]?.status !== OrderStatus.completed &&
      completeClickCount[currentTransactionId] >= 2) ||
    !allCouponApplied
  ) {
    return DEFAULT_TOTAL_COMPLETE_MESSAGE;
  }

  // Completed
  if (currentTransactionId) {
    if (
      transactions[currentTransactionId]?.status === OrderStatus.completed &&
      total
    ) {
      if (alreadySpokenMessage) {
        return alreadySpokenMessage;
      }
      return message
        .split(TOTAL_PLACEHOLDER)
        .join(formatCurrency(total, restaurantCode));
    }

    return DEFAULT_TOTAL_COMPLETE_MESSAGE;
  }
  return DEFAULT_TOTAL_COMPLETE_MESSAGE;
}

/**
 * HITL-1570: should the donation upsell be skipped outright? True only when
 * the constraint declares `speak_total_in_upsell` and no check total exists
 * yet — such a prompt speaks the total, so without one it is never offered,
 * and the caller must skip its side effects (the cart add) too. The `{total}`
 * substitution and the spoken-total bookkeeping live in prepareSpokenPrompt /
 * the sendSpokenPrompt thunk (HITL-1587); a constraint without the condition
 * is never skipped here (the send path's own no-total gate still applies).
 */
export function shouldSkipDonationPrompt({
  speakTotalInUpsell,
  total,
}: {
  speakTotalInUpsell: boolean | undefined;
  total: number | null;
}): boolean {
  return Boolean(speakTotalInUpsell) && !total;
}

/**
 * HITL-1429: resolve the placeholders a brand-menu prompt can carry, so no
 * prompt is ever shown or spoken with raw braces in it.
 *
 * This is the pure substitution rule. Every spoken path reaches it through
 * prepareSpokenPrompt / the sendSpokenPrompt thunk (HITL-1587 extracted the
 * omnibar's formerly-inline copy); the AI-suggestion path also calls it
 * directly for the two places it needs the resolved string without speaking:
 * the agent-facing card text, and the `spoken_text` echoed back to the AI.
 *
 * `{total}` is left in place when there is no total: deciding that case
 * belongs to the send path (prepareSpokenPrompt skips the prompt outright
 * rather than speaking a half-resolved line).
 */
export function resolveTtsPlaceholders({
  message,
  total,
  restaurantCode,
  restaurantName,
}: {
  message: string;
  total: number | null;
  restaurantCode: string;
  restaurantName: string;
}): string {
  let resolved = message.split('{RESTAURANT_NAME}').join(restaurantName);
  if (resolved.includes(TOTAL_PLACEHOLDER) && total) {
    resolved = resolved
      .split(TOTAL_PLACEHOLDER)
      .join(formatCurrency(total, restaurantCode));
  }
  return resolved;
}

/**
 * HITL-1587: THE dollars-string → integer-cents conversion for the order
 * slice's amounts ('18.95' → 1895). selectOrderValues and the spoken-prompt
 * thunk both use it; it lives here rather than in selectors/order because the
 * thunk's callers (menuSlice) cannot import that module — its selector chain
 * re-enters the slice at module init.
 */
export function orderTotalToCents(total: string | null | undefined) {
  return total && total.length
    ? parseInt((Number(total) * 100).toFixed(0))
    : null;
}

/**
 * HITL-1587: THE gate + substitution for every sender of a spoken brand-menu
 * prompt — reached through the sendSpokenPrompt thunk by the omnibar's
 * `sendMessage`, the cart-add auto-prompt (menuSlice), and the center-pane
 * mod-group heading (useTTSMessage). The latter two spoke a
 * `{total}`-templated donation prompt with the placeholder left raw in
 * production (sessions 53a917b1/6edd2ea7/3e9ace8c, 2026-09-03).
 *
 * Resolves `{RESTAURANT_NAME}` and `{total}`. A `{total}`-templated prompt
 * with no check total is skipped outright (`skip: true`) — braces are never
 * spoken. `totalSpoken` reports whether the check total was actually
 * substituted, so callers mark it spoken and Complete skips the duplicate
 * total readback.
 */
export function prepareSpokenPrompt({
  message,
  total,
  restaurantCode,
  restaurantName,
}: {
  message: string;
  total: number | null;
  restaurantCode: string;
  restaurantName: string;
}): { message: string; skip: boolean; totalSpoken: boolean } {
  const hasTotalPlaceholder = message.includes(TOTAL_PLACEHOLDER);
  if (hasTotalPlaceholder && !total) {
    return { message, skip: true, totalSpoken: false };
  }
  return {
    message: resolveTtsPlaceholders({
      message,
      total,
      restaurantCode,
      restaurantName,
    }),
    skip: false,
    totalSpoken: hasTotalPlaceholder,
  };
}

export function formatCurrency(total: number, restaurantCode: string) {
  let currency = printCurrency(total, 'USD').replace('$', '');
  const shouldPrintLongTotalMessage = printLongTotalMessage(restaurantCode);
  if (shouldPrintLongTotalMessage) {
    const [dollarAmount, centAmount] = currency
      .split('.')
      .map((value) => parseInt(value));

    // if dollarAmount == 0 or dollarAmount == 1 then print `dollar` else `dollars`
    currency = `${dollarAmount} ${dollarAmount <= 1 ? 'dollar' : 'dollars'}`;

    // Ignore centAmount == 0
    if (centAmount === 1) {
      currency += ` ${centAmount} cent`;
    } else if (centAmount > 1) {
      currency += ` ${centAmount} cents`;
    }
  } else {
    currency = currency
      .replace('.', ' ')
      .replace(' 00', total === 100 ? ' dollar' : ' dollars') // if total is trailing 00, we suffix “dollars” at the end.
      .replace(' 0', ' ' + OH + ' '); // Read back with an “Oh” if there is a zero directly after the currency decimal mark.
  }

  return currency;
}

export function printLongTotalMessage(restaurantCode: string) {
  let shouldPrintLongTotalMessage = false;
  if (restaurantCode) {
    for (let i = 0; i < BRANDS_REQUIRE_LONG_MESSAGE_WITH_TOTAL.length; i++) {
      const brandKeyword = BRANDS_REQUIRE_LONG_MESSAGE_WITH_TOTAL[i];
      if (restaurantCode.includes(brandKeyword)) {
        shouldPrintLongTotalMessage = true;
        break;
      }
    }
  }
  return shouldPrintLongTotalMessage;
}

function isSizeModifier(modifierName: string) {
  return SIZE_MODIFIERS.find(
    (s) => s.toLowerCase() === modifierName.toLowerCase()
  );
}

function getCartItemNameWithModifiers(
  cartItem: CartItem,
  codeNameMapping: ModSymbolCodeNameMappingType
) {
  let name = cartItem.name.includes('/upsell') ? '' : cartItem.name;

  let originSortedChildModifierGroups = sortChildrenModGroup(cartItem).filter(
    (modGroup) => Object.keys(modGroup.selectedItems).length > 0
  );

  const childModifierGroups: CartModifierGroup[] = [];
  let sizeModifierGroup: CartModifierGroup | null = null;

  originSortedChildModifierGroups.forEach((modifierGroup) => {
    let isSizeGroup = modifierGroup.name?.toLowerCase().includes('size');
    if (!isSizeGroup) {
      const selectedItems = Object.values(modifierGroup.selectedItems || {});
      if (selectedItems.length > 0) {
        isSizeGroup = selectedItems.every((selectedItem) => {
          return isSizeModifier(selectedItem.name || '');
        });
      }
    }

    if (isSizeGroup) sizeModifierGroup = modifierGroup;
    else childModifierGroups.push(modifierGroup);
  });

  if (childModifierGroups.length > 0) {
    name += cartItem.name.includes('/upsell') ? '' : ' with';
    childModifierGroups.forEach((modGroup, modIndex) => {
      const selectedItems = Object.values(modGroup.selectedItems || {});
      if (selectedItems.length === 1 && selectedItems[0].name === 'DECLINED')
        return '';

      selectedItems.forEach((selectedItem, index) => {
        let toAdd = !selectedItem.name.includes('!!')
          ? ` ${selectedItem.name}`
          : '';
        let rep = Object.keys(READ_REP_MAP).filter(
          (k) => k.toLowerCase() == selectedItem.name.toLowerCase()
        );
        if (rep[0]) {
          toAdd = toAdd.replace(rep[0].toLowerCase(), READ_REP_MAP[rep[0]]);
        }
        let getModName = (mcName: string, modName: string) =>
          (codeNameMapping[mcName]?.speak?.(modName) ||
            codeNameMapping[mcName]?.name) + ` ${toAdd}`;
        toAdd = selectedItem.modcode
          ? getModName(selectedItem.modcode, toAdd)
          : '' + toAdd;
        if (toAdd) {
          name += toAdd;
          if (
            modIndex < childModifierGroups.length - 1 ||
            index < selectedItems.length - 1
          ) {
            name += ',';
          }
        }
      });
    });
  }

  if (sizeModifierGroup) {
    const selectedItems = Object.values(
      (sizeModifierGroup as CartModifierGroup).selectedItems || {}
    );
    if (selectedItems.length > 0) {
      const sizeModifier = SIZE_MODIFIERS.find((sizeOption) =>
        selectedItems[0].name.toLowerCase().includes(sizeOption.toLowerCase())
      );
      name = sizeModifier + ' ' + name;
    }
  }
  return name;
}

export default function useGetRepeatMessage() {
  const { cartItems, cartItemsQuantity } = useSelector(
    (state: RootState) => state.cart
  );
  const codeNameMapping = useShallowSelector(getCodeNameMapping);

  if (Object.values(cartItems).length === 0) {
    return 'There’s no items in your order.';
  }

  const cartNameToCnt: Record<string, number> = {};
  Object.values(cartItems).forEach((item) => {
    const name = getCartItemNameWithModifiers(item, codeNameMapping);
    cartNameToCnt[name] =
      (cartNameToCnt[name] || 0) + (cartItemsQuantity[item.cartItemId] || 1);
  });

  let message = '';
  const totalCartLength = Object.keys(cartNameToCnt).filter((k) => k).length;
  Object.keys(cartNameToCnt)
    .filter((k) => k)
    .forEach((name, index) => {
      const cnt = cartNameToCnt[name];
      if (cnt === 1) {
        message += `1 ${name}`;
      } else {
        message += `${cnt} ${name}`;
      }

      if (index === totalCartLength - 2) {
        message += ', and ';
      }
      if (index < totalCartLength - 2) {
        message += ', ';
      }
    });

  return message.trim() ? `I have ${message}, is that correct?` : '';
}

export const getItemUnavailableMessage = (itemName: string) =>
  `I'm sorry, but ${itemName} is unavailable`;
